import { escapeHTML as esc, root, json, icon } from "@theme/elumen-commerce";

export const families = ["COOLS", "Naturals", "PURES", "WARMS"];
export const familyNames = {
  COOLS: "Kühle Töne",
  Naturals: "Naturtöne",
  PURES: "Intensive Töne",
  WARMS: "Warme Töne",
};
export const initialState = () => ({
  base: null,
  selected: [],
  step: 1,
  opened: [],
  pendingBase: null,
});
export function transition(state, action) {
  switch (action.type) {
    case "base":
      if (state.base === action.id) return state;
      if (state.selected.length) return { ...state, pendingBase: action.id };
      return {
        ...state,
        base: action.id,
        selected: [],
        opened: [],
        pendingBase: null,
      };
    case "confirm-base":
      return {
        ...state,
        base: state.pendingBase,
        pendingBase: null,
        selected: [],
        opened: [],
        step: 1,
      };
    case "cancel-base":
      return { ...state, pendingBase: null };
    case "step":
      return action.step === 1 ||
        (action.step === 2 && state.base) ||
        (action.step === 3 && state.base && state.selected.length)
        ? { ...state, step: action.step }
        : state;
    case "select":
      return state.selected.includes(action.id) || state.selected.length >= 2
        ? state
        : { ...state, selected: [...state.selected, action.id] };
    case "remove":
      return {
        ...state,
        selected: state.selected.filter((id) => id !== action.id),
      };
    case "clear":
      return { ...state, selected: [] };
    default:
      return state;
  }
}
const guidance =
  "Die Darstellung dient der digitalen Orientierung. Farben können je nach Bildschirm, Haarstruktur und Ausgangssituation abweichen. Eine Probesträhne wird empfohlen.";
const baseNames = {
  "02": "Schwarz",
  "03": "Dunkelbraun",
  "04": "Braun",
  "05": "Hellbraun",
  "06": "Dunkelblond",
  "07": "Mittelblond",
  "08": "Hellblond",
  "09": "Sehrhellblond",
  10: "Lichtblond",
};
const descriptions = {
  COOLS: "Aschige, kühle Ergebnisse",
  Naturals: "Natürliche, ausgewogene Farbergebnisse",
  PURES: "Leuchtende, intensive Farbergebnisse",
  WARMS: "Warme, goldene und rötliche Ergebnisse",
};
const image = (item, small = false) =>
  `<span class="ew-image${small ? " ew-image--small" : ""}"><img src="${esc(item.image)}" alt="${small ? "" : esc(item.name)}" ${small ? "" : 'loading="lazy"'}><span hidden>Bild nicht verfügbar</span></span>`;
const button = (label, action, disabled = false, extra = "") =>
  `<button type="button" data-action="${action}" ${disabled ? "disabled" : ""} ${extra}>${label}</button>`;
const next = (label) => `${label}${icon("caret", "ew-next")}`;
const scrollTrack =
  '<div class="ew-scroll-track" aria-hidden="true"><span></span></div>';
const baseTitle = (tone) =>
  `${esc(tone.id)} ${baseNames[tone.id] || esc(tone.name)}`;

class ElumenWizard extends HTMLElement {
  state = initialState();
  showProducts = false;
  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }
  updateScrollTrack(strip) {
    const track = strip.nextElementSibling;
    if (!track?.matches(".ew-scroll-track")) return;
    const width = strip.clientWidth;
    const total = strip.scrollWidth;
    track.hidden = !width || total <= width + 1;
    if (track.hidden) return;
    const thumb = track.firstElementChild;
    thumb.style.width = `${(width / total) * 100}%`;
    thumb.style.left = `${(Math.max(0, Math.min(strip.scrollLeft, total - width)) / total) * 100}%`;
  }
  updateScrollTracks() {
    this.querySelectorAll(".ew-bases, .ew-colors").forEach((strip) =>
      this.updateScrollTrack(strip),
    );
  }
  async connectedCallback() {
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.updateScrollTracks());
      this.resizeObserver.observe(this);
    }
    if (this.initialized) return;
    this.initialized = true;
    this.addEventListener("click", this.click);
    this.addEventListener(
      "scroll",
      (event) => {
        if (event.target.matches(".ew-bases, .ew-colors"))
          this.updateScrollTrack(event.target);
      },
      true,
    );
    this.addEventListener("change", (event) => {
      if (event.target.matches('[name="elumen-base"]'))
        this.chooseBase(event.target.value);
    });
    this.addEventListener(
      "error",
      (event) => {
        if (
          event.target.tagName === "IMG" &&
          event.target.closest(".ew-image")
        ) {
          event.target.hidden = true;
          event.target.nextElementSibling.hidden = false;
        }
      },
      true,
    );
    await this.load();
  }
  async load() {
    this.innerHTML = '<p role="status">Ausgangstöne werden geladen …</p>';
    try {
      const data = await json(this.dataset.catalog);
      if (!Array.isArray(data.baseTones) || !data.targets) throw new Error();
      this.catalog = data;
      this.render();
    } catch {
      this.innerHTML = `<p role="alert">Die Ausgangstöne konnten nicht geladen werden.</p>${button("Erneut versuchen", "retry")}`;
    }
  }
  get tones() {
    return Object.values(this.catalog.targets)
      .flat()
      .filter((item) => item.baseTone === this.state.base);
  }
  get selected() {
    return this.state.selected
      .map((id) => this.tones.find((item) => item.name === id))
      .filter(Boolean);
  }
  send(action) {
    const oldBase = this.state.base;
    this.state = transition(this.state, action);
    if (oldBase !== this.state.base) this.openedOnce = false;
  }
  message(text) {
    this.querySelector("[data-status]").textContent = text;
  }
  navigate(step) {
    this.send({ type: "step", step });
    this.showProducts = step === 3 && this.selected.length === 1;
    this.render();
    this.querySelector("[data-step-title]")?.focus({ preventScroll: true });
    this.scrollIntoView({ block: "start" });
  }
  openBaseDialog() {
    const dialog = this.querySelector("dialog");
    this.returnFocus = document.activeElement;
    dialog.showModal();
    dialog.querySelector('[data-action="cancel-base"]').focus();
  }
  chooseBase(id) {
    this.send({ type: "base", id });
    if (!this.state.pendingBase) {
      this.render();
      this.querySelector(`[value="${CSS.escape(id)}"]`)?.focus({
        preventScroll: true,
      });
      return;
    }
    this.querySelectorAll('[name="elumen-base"]').forEach((input) => {
      input.checked = input.value === this.state.base;
    });
    this.openBaseDialog();
  }
  closeBase(confirm) {
    this.querySelector("dialog").close();
    if (confirm) {
      if (this.state.pendingBase) this.send({ type: "confirm-base" });
      else this.state = { ...initialState(), base: this.state.base };
      this.showProducts = false;
      this.openedOnce = false;
      this.render();
      this.querySelector("[data-step-title]")?.focus({ preventScroll: true });
    } else {
      this.send({ type: "cancel-base" });
      this.returnFocus?.focus({ preventScroll: true });
    }
  }
  click = (event) => {
    const target = event.target.closest("[data-action]");
    if (!target || !this.contains(target)) return;
    const action = target.dataset.action;
    if (action === "retry") return this.load();
    if (action === "step") return this.navigate(Number(target.dataset.step));
    if (action === "edit-base") {
      if (!this.selected.length) return this.navigate(1);
      this.openBaseDialog();
      return;
    }
    if (action === "confirm-base" || action === "cancel-base")
      return this.closeBase(action === "confirm-base");
    if (action === "products") {
      this.showProducts = true;
      this.render();
      this.querySelector("[data-step-title]")?.focus({ preventScroll: true });
      this.scrollIntoView({ block: "start" });
      return;
    }
    if (action === "restart") {
      this.state = initialState();
      this.showProducts = false;
      this.render();
      return;
    }
    if (action === "select" || action === "remove" || action === "clear") {
      const id = target.dataset.id;
      if (action === "select" && this.state.selected.length === 2)
        return this.message(
          "Du kannst höchstens zwei Nuancen vergleichen. Entferne zuerst eine ausgewählte Nuance.",
        );
      this.send({ type: action, id });
      this.render();
      const nextControl = [...this.querySelectorAll("[data-action]")].find(
        (el) =>
          el.dataset.id === id &&
          el.dataset.action === (action === "select" ? "remove" : "select"),
      );
      (
        nextControl ||
        this.querySelector("[data-selection-title]") ||
        this.querySelector("[data-step-title]")
      )?.focus({ preventScroll: true });
      this.message(`${this.state.selected.length} von 2 Nuancen ausgewählt.`);
    }
  };
  context(basis) {
    return `<div class="ew-context">${image(basis, true)}<span>Deine Basis:<strong>${baseTitle(basis)}</strong></span>${button("Ändern", "edit-base", false, 'class="ew-soft"')}</div>`;
  }
  selection() {
    return `<aside class="ew-selection" aria-label="Deine Farbauswahl"><h3 data-selection-title tabindex="-1">Deine Auswahl · <em>${this.state.selected.length} von 2</em></h3><div class="ew-chips">${this.selected.map((item) => `<div class="ew-chip">${image(item, true)}<span><strong>${esc(item.name)}</strong><small>${familyNames[item.family]}</small></span>${button(`${icon("close")}<span>Entfernen</span>`, "remove", false, `data-id="${esc(item.name)}" aria-label="Nuance ${esc(item.name)} entfernen"`)}</div>`).join("")}</div>${button(next(this.selected.length === 2 ? "Weiter zum Vergleich" : "Weiter zum Produkt"), "step", !this.selected.length, 'data-step="3" class="ew-primary"')}<div class="ew-next-note"><strong>Nächster Schritt</strong><p>${this.selected.length === 2 ? "Im Vergleich siehst du deine ausgewählten Nuancen direkt nebeneinander." : "Sieh dir deine Wunschfarbe an und entdecke das passende Elumen-Produkt."}</p></div></aside>`;
  }
  baseView() {
    const tones = this.catalog.baseTones;
    return `<h2 tabindex="-1" data-step-title>Wie sieht dein Haar jetzt aus?</h2><p class="ew-intro">Vergleiche deine Haarlängen bei Tageslicht.</p><fieldset class="ew-bases"><legend class="visually-hidden">Ausgangston wählen</legend>${tones.map((tone) => `<label class="ew-base"><input type="radio" name="elumen-base" value="${esc(tone.id)}" ${this.state.base === tone.id ? "checked" : ""}>${image(tone)}<span>${baseTitle(tone)}</span><span class="ew-check">${icon("checkmark")}</span></label>`).join("")}</fieldset>${scrollTrack}<details class="ew-unsure"><summary><strong>Ich bin unsicher.</strong><span>Hier findest du Tipps zur Bestimmung deines Ausgangstons.</span>${icon("caret", "ew-next")}</summary><p>Vergleiche deine Haarlängen bei Tageslicht mit den Originalsträhnen. Bei unterschiedlichen Haarbereichen, Vorfärbung oder Unsicherheit hilft eine professionelle Beratung.</p></details>${button(next("Weiter zur Wunschfarbe"), "step", !this.state.base, 'data-step="2" class="ew-primary ew-continue"')}`;
  }
  colorsView() {
    const { selected } = this.state;
    return `<h2 tabindex="-1" data-step-title>Welche Nuance gefällt dir?</h2><p class="ew-intro">Wähle die Nuance, die dir am besten gefällt.</p>${families
      .map((key) => {
        const tones = this.tones.filter((item) => item.family === key);
        const coverName = {
          COOLS: "AS@9",
          Naturals: "NN@5",
          PURES: "RR@all",
          WARMS: "KB@7",
        }[key];
        const cover =
          tones.find((item) => item.name === coverName) ||
          this.catalog.targets[key].find((item) => item.name === coverName) ||
          tones[0];
        return `<details class="ew-family" name="elumen-family-${this.dataset.sectionId}" data-family="${key}" ${this.state.opened.includes(key) ? "open" : ""}><summary>${cover ? image(cover, true) : ""}<span><strong>${familyNames[key]}</strong><small>${descriptions[key]}</small></span>${icon("caret")}</summary><div class="ew-colors">${
          tones.length
            ? tones
                .map(
                  (item) =>
                    `<article class="ew-card ${selected.includes(item.name) ? "is-selected" : ""}">${image(item)}${selected.includes(item.name) ? `<span class="ew-check">${icon("checkmark")}</span>` : ""}<h3>${esc(item.name)}</h3>${button(`${icon(selected.includes(item.name) ? "checkmark" : "add-to-cart")}${selected.includes(item.name) ? "Ausgewählt" : "Auswählen"}`, selected.includes(item.name) ? "remove" : "select", false, `data-id="${esc(item.name)}" aria-label="Nuance ${esc(item.name)} ${selected.includes(item.name) ? "entfernen" : "auswählen"}"`)}</article>`,
                )
                .join("")
            : "<p>Für diesen Ausgangston sind hier keine Nuancen verfügbar.</p>"
        }</div>${scrollTrack}</details>`;
      })
      .join("")}`;
  }
  resultView() {
    if (!this.selected.length)
      return `<h2 tabindex="-1" data-step-title>Deine Auswahl ist leer.</h2><p>Wähle deine Wunschfarbe aus.</p>${button("Nuancen auswählen", "step", false, 'data-step="2"')}`;
    if (this.showProducts)
      return `<h2 tabindex="-1" data-step-title>Deine Farbe. Direkt im Warenkorb.</h2><p class="ew-intro">Überprüfe deine Auswahl und lege das Produkt in den Warenkorb.</p><div class="ew-products">${this.selected.map((item) => (item.shopUrl && item.variantId && item.purchaseVerified !== false ? `<elumen-purchase data-shade="${esc(item.name)}" data-handle="${esc(new URL(item.shopUrl).pathname.split("/").pop())}" data-variant="${esc(item.variantId)}"></elumen-purchase>` : `<p>Für ${esc(item.name)} ist die Produktzuordnung nicht bestätigt. Die Farbansicht bleibt verfügbar.</p>`)).join("")}</div><div data-care-slot></div><div class="ew-result-actions"><a class="ew-primary ew-cart-link" href="${root()}cart">${icon("cart")}Warenkorb ansehen${icon("caret", "ew-next")}</a></div>`;
    return `<h2 tabindex="-1" data-step-title>Deine Auswahl im Vergleich</h2><p class="ew-intro">Hier siehst du die gewählten Nuancen im direkten Vergleich.</p><div class="ew-compare">${this.selected.map((item) => `<article class="ew-card">${image(item)}<h3>${esc(item.name)}</h3><p>${descriptions[item.family]}</p>${button("Ändern", "step", false, 'data-step="2" class="ew-soft"')}${button(`${icon("close")}Entfernen`, "remove", false, `class="ew-remove" data-id="${esc(item.name)}" aria-label="Nuance ${esc(item.name)} entfernen"`)}</article>`).join("")}</div><p class="ew-guidance">Die Darstellung ist eine Orientierung und keine Vorschau eines garantierten Farbergebnisses.</p><div class="ew-result-actions">${button(next("Passende Produkte ansehen"), "products", false, 'class="ew-primary"')}${button("Weitere Nuance wählen", "step", this.selected.length >= 2, 'data-step="2"')}<small>Maximal 2 Farben möglich.</small>${button("Neu starten", "restart", false, 'class="ew-text-button"')}</div>`;
  }
  render() {
    if (this.querySelector("details[data-family]"))
      this.state.opened = [
        ...this.querySelectorAll("details[data-family][open]"),
      ].map((el) => el.dataset.family);
    const { step, base, selected } = this.state;
    this.dataset.step = step;
    this.dataset.view = step === 3 && this.showProducts ? "products" : "colors";
    const basis = this.catalog.baseTones.find((tone) => tone.id === base);
    if (step === 2 && !this.state.opened.length && !this.openedOnce) {
      this.state.opened = [
        this.tones.some((item) => item.family === "Naturals")
          ? "Naturals"
          : families.find((key) =>
              this.tones.some((item) => item.family === key),
            ),
      ].filter(Boolean);
      this.openedOnce = true;
    }
    this.colorScrollPositions ||= {};
    this.querySelectorAll("details[data-family][open]").forEach((family) => {
      this.colorScrollPositions[family.dataset.family] =
        family.querySelector(".ew-colors").scrollLeft;
    });
    const baseStrip = this.querySelector(".ew-bases");
    if (baseStrip) this.baseScrollLeft = baseStrip.scrollLeft;
    const purchaseCards = new Map(
      [...this.querySelectorAll("elumen-purchase")].map((el) => [
        el.dataset.variant,
        el,
      ]),
    );
    this.innerHTML = `<header class="ew-header"><h1>${step === 1 ? "Deine Elumen-Farbkarte" : "Digitale Elumen-Farbkarte"}</h1><p>Entdecke Nuancen passend zu deinem Ausgangston.</p></header><nav aria-label="Schritte der Farbkarte"><ol class="ew-steps">${["Ausgangston", "Wunschfarbe", selected.length === 1 ? "Dein Produkt" : "Vergleich"].map((name, index) => `<li class="${index + 1 <= step ? "is-reached" : ""}">${button(`<span class="ew-step-number">${index + 1 < step ? icon("checkmark") : index + 1}</span><span>${name}</span>`, "step", (index === 1 && !base) || (index === 2 && !selected.length), `data-step="${index + 1}" ${step === index + 1 ? 'aria-current="step"' : ""}`)}</li>`).join("")}</ol></nav><p class="ew-status" data-status role="status" aria-live="polite"></p><div class="ew-layout${step === 2 ? " ew-layout--selection" : ""}"><section class="ew-main">${basis && step !== 1 && !(step === 3 && this.showProducts) ? this.context(basis) : ""}${step === 1 ? this.baseView() : step === 2 ? this.colorsView() : this.resultView()}</section>${step === 2 ? this.selection() : ""}</div><div class="ew-help"><details><summary><span><strong>So funktioniert’s</strong><small>Wähle deinen Ausgangston, entdecke passende Elumen-Nuancen und vergleiche die Originalsträhnen direkt nebeneinander.</small></span>${icon("caret")}</summary><p>Wähle deinen Ausgangston, entdecke passende Elumen-Nuancen und vergleiche die Originalsträhnen direkt nebeneinander.</p></details><details><summary><span><strong>Digitale Orientierung</strong><small>Farben können je nach Bildschirm, Haarstruktur und Ausgangssituation abweichen.</small></span>${icon("caret")}</summary><p>${guidance}</p></details></div><p class="ew-footnote">Digitale Orientierung · Keine Ergebnisgarantie · Probesträhne empfohlen.</p><dialog aria-labelledby="ew-dialog-title-${this.dataset.sectionId}">${button(icon("close"), "cancel-base", false, 'class="ew-dialog-close" aria-label="Schließen"')}<h2 id="ew-dialog-title-${this.dataset.sectionId}">Ausgangston ändern?</h2><p>Deine ausgewählten Farben werden dabei zurückgesetzt.</p>${basis ? `<div class="ew-dialog-base">${image(basis, true)}<span>Aktueller Ausgangston:<strong>${baseTitle(basis)}</strong></span></div>` : ""}<div class="ew-dialog-actions">${button(next("Ausgangston neu wählen"), "confirm-base", false, 'class="ew-primary"')}${button("Auswahl behalten", "cancel-base")}</div></dialog>`;
    this.querySelectorAll("elumen-purchase").forEach((el) => {
      const previous = purchaseCards.get(el.dataset.variant);
      if (previous) el.replaceWith(previous);
    });
    const careTemplate = this.parentElement.querySelector(
      "[data-elumen-care-template]",
    );
    const slot = this.querySelector("[data-care-slot]");
    if (slot && careTemplate) slot.append(careTemplate.content.cloneNode(true));
    this.querySelector("dialog").addEventListener("cancel", (event) => {
      event.preventDefault();
      this.closeBase(false);
    });
    this.querySelectorAll("details[data-family]").forEach((details) =>
      details.addEventListener("toggle", () => {
        this.state.opened = [
          ...this.querySelectorAll("details[data-family][open]"),
        ].map((el) => el.dataset.family);
        this.updateScrollTracks();
      }),
    );
    this.querySelectorAll("details[data-family][open]").forEach((family) => {
      family.querySelector(".ew-colors").scrollLeft =
        this.colorScrollPositions[family.dataset.family] || 0;
    });
    const nextBaseStrip = this.querySelector(".ew-bases");
    if (nextBaseStrip) nextBaseStrip.scrollLeft = this.baseScrollLeft || 0;
    this.updateScrollTracks();
    if (!base) this.openedOnce = false;
  }
}
if (!customElements.get("elumen-wizard"))
  customElements.define("elumen-wizard", ElumenWizard);
